import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { HelpOutline, SaveOutlined, ShareOutlined } from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Card, IconButton, SxProps, Theme, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';

import { taskFormsApi } from '../../axios';
import { useLanguage, useProjectConfig } from '../../hooks';
import { datascopeTracker } from '../../utils/eventTracker';
import { paths } from '../../utils/navigation';
import { ACTIVITY_PERIOD_TIME_INCOMPLETE } from './activityPeriodTimeValidation';
import { ResponsiveIconButton } from '../uiComponents';
import { Input } from '../uiComponents/ComponentLibrary/Input';

interface HeaderProps {
  formName: string;
  onNameChange: (name: string) => void;
  originalFormName: string;
  helper?: { title: string; url: string };
  disableButton: boolean;
  onSave: () => Promise<void>;
  onShare: () => void;
  formId?: string;
}

const customStyles: Record<string, SxProps<Theme>> = {
  header: {
    padding: '14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.09)',
  },
  formNameContainer: {
    flexGrow: 1,
    maxWidth: '480px',
  },
  btnsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  divider: {
    borderLeft: '1px solid #DDE0E5',
    height: '32px',
  },
  iconBtn: {
    padding: '6px',
    borderRadius: '8px',
    border: '1px solid #D1D4D9',
    color: '#3B3C40',
  },
};

export const Header = ({
  formName,
  onNameChange,
  originalFormName,
  helper,
  disableButton,
  onSave,
  onShare,
  formId,
}: HeaderProps) => {
  const navigate = useNavigate();
  const { blockFormBuilder } = useProjectConfig();
  const { enqueueSnackbar } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);

  const lang = useLanguage();
  const isNewForm = formId === 'new';
  const saveButtonDisabled = blockFormBuilder || disableButton || isSaving;

  const handleSaveForm = async () => {
    if (isSaving) return false;

    setIsSaving(true);

    try {
      const { valid } = await taskFormsApi.verifyFormName(formId, formName.trim());
      if (!valid) throw 'nameExists';
      await onSave();
      return true;
    } catch (error) {
      // A save blocked by incomplete activity questions already showed its own
      // message, which says what is missing. Stacking the generic one on top
      // only adds noise over the only message that helps the user.
      if (error !== ACTIVITY_PERIOD_TIME_INCOMPLETE) {
        enqueueSnackbar(
          error === 'nameExists' ? lang.form_builder.formNameExists : lang.form_builder.saveError,
          { variant: 'error' },
        );
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleFillForm = async () => {
    const formSaved = await handleSaveForm();
    if (formSaved || blockFormBuilder)
      navigate(`../${paths.forms.newAnswer.replace(':formId', formId)}`);
  };

  return (
    <Card sx={customStyles.header}>
      <Box sx={customStyles.formNameContainer}>
        <Input
          fullWidth
          variant='outlined'
          disabled={blockFormBuilder}
          label={lang.form_builder.formName}
          value={formName}
          required
          error={!formName}
          onChange={(e) => onNameChange(e.target.value)}
          onBlur={(e) => {
            if (e.target.value === '') onNameChange(originalFormName);
          }}
          inputProps={{
            maxLength: 255,
          }}
        />
      </Box>
      <Box sx={customStyles.btnsContainer}>
        <Tooltip
          title={lang.form_builder.howToCreate}
          arrow
        >
          <IconButton
            sx={customStyles.iconBtn}
            onClick={() => helper && window.open(helper.url)}
            size='large'
          >
            <HelpOutline fontSize='small' />
          </IconButton>
        </Tooltip>
        {!isNewForm && (
          <Tooltip
            title={lang.form_builder.share}
            arrow
          >
            <IconButton
              sx={customStyles.iconBtn}
              onClick={() => onShare()}
              size='large'
            >
              <ShareOutlined fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={customStyles.divider}></Box>
        <ResponsiveIconButton
          endIcon={<SaveOutlined color={saveButtonDisabled ? 'disabled' : 'inherit'} />}
          icon={<SaveOutlined color={saveButtonDisabled ? 'disabled' : 'inherit'} />}
          message={lang.form_builder.saveForm}
          disabled={saveButtonDisabled}
          onClick={handleSaveForm}
          variant={isNewForm ? 'contained' : 'outlined'}
          color={'primary'}
          breakpoint={768}
        />
        {!isNewForm && (
          <ResponsiveIconButton
            disabled={disableButton}
            message={lang.tasksForms.answer_form}
            onClick={() => {
              datascopeTracker(
                'Click Fill Out on Form Builder',
                { source: 'FORM_BUILDER', formId },
                'AMP',
              );
              handleFillForm();
            }}
            icon={<ArrowForwardIcon color='primary' />}
            endIcon={<ArrowForwardIcon />}
            color={'primary'}
            variant={'contained'}
            breakpoint={768}
          />
        )}
      </Box>
    </Card>
  );
};
